import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';

export type RevenueModel = {
  /** Billing row per firm, keyed on the company name the usage rows are reported under. */
  byFirm: Map<string, BillingDetail>;
};

/**
 * Both sides key on Company.name — usage rows are built with `company=company.name` and
 * billing rows expose the same field as `companyName` — so the join is exact, not fuzzy.
 */
export function buildRevenueModel(
  details: readonly BillingDetail[] | undefined,
  firmNames: readonly string[],
  showInternal = false,
): RevenueModel | null {
  if (!details) return null;

  const names = new Set(firmNames);
  const byFirm = new Map<string, BillingDetail>();
  for (const detail of details) {
    if ((showInternal || !detail.isInternal) && names.has(detail.companyName)) {
      byFirm.set(detail.companyName, detail);
    }
  }
  return { byFirm };
}

/**
 * How much of a firm's stated book is actually on the platform: units (families, counting a
 * client in no family as one) against the families the firm says it has.
 */
export function percentOfStated(count: number, stated: number | null): number | null {
  if (stated === null || stated <= 0) return null;
  return (count / stated) * 100;
}

export function formatPercent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}%`;
}

export function billingFor(
  revenue: RevenueModel | null,
  firmName: string,
): BillingDetail | undefined {
  return revenue?.byFirm.get(firmName);
}
